import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEnvelope } from '@fortawesome/free-solid-svg-icons'
import { SubmitHandler, useForm } from 'react-hook-form'
import { toast } from 'react-toastify'
import { yupResolver } from '@hookform/resolvers/yup'
import { object, boolean } from 'yup'
import { Validation } from '../../common/types'
import { Subscribe, UserValidationRules } from '@ringo/shared'
import React, { useRef } from 'react'
import { useMutation } from '@tanstack/react-query'
import { useFooterNewsletterApi } from '../../common/api-context'
import ControlledInputText from '../form/controlled-input-text'
import ControlledCheckbox from '../form/controlled-checkbox'
import HCaptcha from '@hcaptcha/react-hcaptcha'
import { Captcha } from '../common/captcha'
import { GermanLangKey, useTranslation } from '../../useTranslation'
import I18nText from '../common/i18n-text'

interface NewsletterProps {
    responsive?: boolean
}

const NewsletterForm: React.FC<NewsletterProps> = ({ responsive = false }) => {
    const footerNewsletterApi = useFooterNewsletterApi()
    const { t, i18n } = useTranslation()
    const captchaRef = useRef<HCaptcha>(null)

    const validationObject: Validation<Subscribe> = {
        email: UserValidationRules.email(),
        privacyPolicyAccepted: boolean().required({ key: 'validations.mustAccept' }).oneOf([true], { key: 'validations.mustAccept' }),
    }

    const schema = object(validationObject)

    const { handleSubmit, control, reset } = useForm<Subscribe>({
        resolver: yupResolver(schema as any),
        defaultValues: {
            email: '',
            privacyPolicyAccepted: false,
        },
    })

    const subscribeUser = useMutation({
        mutationFn: (formData: Subscribe) => {
            return footerNewsletterApi.subscribe(formData)
        },
        onSuccess: () => {
            toast.success(t('footer.newsletter.newsletterSuccessSubmit'))
            reset()
        },
    })

    const onSubmit: SubmitHandler<Subscribe> = async (data) => {
        if (!data.privacyPolicyAccepted) {
            toast.error(t('footer.newsletter.newsletterConsentValidation'))
        } else {
            const { response } = await captchaRef.current!.execute({ async: true })

            subscribeUser.mutate({ ...data, hcaptchaPasscode: response })
        }
    }

    const PrivacyPolicyTranslation = () => {
        return (
            <span className="text-[#8c8c8c] text-sm privacy-policy">
                <I18nText i18nKey="footer.iHaveReadThePrivacyPolicy">
                    I have read the
                    <a
                        href={
                            i18n.language === GermanLangKey ? 'https://www.heimerle-meule.com/de/datenschutz' : 'https://www.heimerle-meule.com/data-protection'
                        }
                        className="underline"
                        target="_blank"
                        data-cy="newsletter-privacy-link"
                    >
                        privacy policy
                    </a>
                    .
                </I18nText>
            </span>
        )
    }

    return (
        <>
            <Captcha ref={captchaRef} />
            <h4 className="pb-4 font-bold leading-5 text-[15px] max-tablet:text-sm">{t('footer.newsletter')}</h4>
            <form onSubmit={handleSubmit(onSubmit)} className={`text-[13px] leading-5  ${responsive && 'max-md:px-5'}`} data-cy="newsletter-form">
                {t('footer.newsletter.subscribeToTheFreeNewsletter')}
                <div className="newsletter-input-holder relative my-3">
                    <ControlledInputText
                        control={control}
                        label={t('yourEmailAddress')}
                        hideLabel
                        name="email"
                        className="newsletter-email-input placeholder-italic border! rounded-xs! bg-gray-50! pt-1.5! px-3! pb-[3px]! text-black"
                        dataCy="newsletter-email"
                    />

                    <button
                        type="submit"
                        className="newsletter-submit absolute right-0 top-[5px] focus:outline-hidden font-medium rounded-lg text-sm px-2.5 z-10 max-tablet:top-[3.5px]"
                        data-cy="newsletter-submit"
                    >
                        <FontAwesomeIcon icon={faEnvelope} className="text-gray-300 text-2xl hover:text-custom_yellow" />
                    </button>
                </div>
                <ControlledCheckbox
                    control={control}
                    name="privacyPolicyAccepted"
                    label={PrivacyPolicyTranslation() as any}
                    dataCy="newsletter-privacy-policy"
                />
            </form>
        </>
    )
}

export default NewsletterForm
